import React, { useState, useMemo, useCallback } from 'react';
import { TreeSelect, Spin } from 'antd';
import LabelDisplay from '../LabelDisplay';
import useMountEffect from '../../hooks/useMountEffect';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';

const { SHOW_PARENT } = TreeSelect;

const DEFAULT_PAGINATION = { current: 1, pageSize: 1000 };
const DEFAULT_SORTING = {};

/**
 * Redux-reactive label filter dropdown.
 *
 * Reads its option list straight from the shared Redux collection that label
 * creation writes to (resolved via `getRecordsForTargetCollection(stateSlice,
 * targetCollectionName)`), so a label created in the management sidebar shows up
 * here instantly without a manual refetch. It still fires `fetchData` on mount to
 * hydrate that collection from the server.
 *
 * This is intentionally separate from the generic `OWAsyncTreeSelect` (which is
 * fetch/cache based and shared by many server-search filters) — keep label
 * filters on this component when instant reactivity to local creates is required.
 */
const LabelsDropdown = ({
	value,
	onChange,
	mode,
	allowClear = true,
	style,
	placeholder,
	disabled,
	tagRender,
	stateSlice,
	targetCollectionName,
	fetchData,
	renderRecord,
}) => {
	const [fetched, setFetched] = useState(false);

	useMountEffect(() => {
		const fetchPromise =
			fetchData &&
			fetchData('', targetCollectionName, DEFAULT_PAGINATION, DEFAULT_SORTING, {}, false);
		if (fetchPromise && typeof fetchPromise.then === 'function') {
			fetchPromise.then(() => setFetched(true)).catch(() => setFetched(true));
		} else {
			setFetched(true);
		}
	});

	const collectionRecords = useMemo(() => {
		if (!stateSlice || !targetCollectionName) {
			return [];
		}
		try {
			return getRecordsForTargetCollection(stateSlice, targetCollectionName) || [];
		} catch (e) {
			// Collection not yet present on the slice (e.g. before first fetch) — render empty.
			return [];
		}
	}, [stateSlice, targetCollectionName]);

	const treeData = useMemo(() => {
		const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
		const baseRecords = stateSlice && stateSlice.records ? stateSlice.records : {};
		const selectedRecords = selectedIds
			.map((id) => baseRecords[id])
			.filter((record) => record && record.id);

		const collectionIds = new Set(collectionRecords.map((label) => label.id));
		const merged = [
			...selectedRecords.filter((record) => !collectionIds.has(record.id)),
			...collectionRecords,
		];

		return merged.map((label) => ({
			value: label.id,
			key: label.id,
			title: renderRecord ? renderRecord(label) : <LabelDisplay tag={label} />,
			filterText: label.label || '',
		}));
	}, [collectionRecords, value, stateSlice, renderRecord]);

	const handleChange = useCallback(
		(selectedValues) => {
			if (onChange) {
				onChange(selectedValues);
			}
		},
		[onChange]
	);

	return (
		<TreeSelect
			virtual={false}
			multiple={mode === 'multiple'}
			treeCheckable={mode === 'multiple'}
			showCheckedStrategy={SHOW_PARENT}
			treeDefaultExpandAll
			notFoundContent={!fetched ? <Spin size="small" /> : null}
			placeholder={placeholder}
			showSearch
			treeNodeFilterProp="filterText"
			onChange={handleChange}
			treeData={treeData}
			style={style}
			allowClear={allowClear}
			value={value}
			disabled={disabled || (!fetched && treeData.length === 0)}
			tagRender={tagRender}
		/>
	);
};

export default LabelsDropdown;
